import * as nodePath from 'node:path';
import { useStore } from '@nanostores/react';
import { Root, Portal, Content, Item, Group, Trigger } from '@radix-ui/react-context-menu';
import { memo, useRef, useState, type ComponentProps, type PropsWithChildren } from 'react';
import { toast } from 'react-toastify';
import { chatStore } from '~/lib/stores/chat';
import { FilesystemError, type Dirent, type FileMap } from '~/lib/stores/files';
import { workbenchStore } from '~/lib/stores/workbench';
import type { Node } from '~/types/file-tree';
import { classNames } from '~/utils/classNames';
import { WORK_DIR } from '~/utils/constants';
import { logger } from '~/utils/logger';

type FileMoveEvent<Method extends string> = { method: Method; oldPath: string; newPath: string };

type FileChangeEvent = { type: Dirent['type'] } & (
  | { method: 'add' | 'remove'; path: string }
  | FileMoveEvent<'rename'>
  | FileMoveEvent<'copy'>
  | FileMoveEvent<'cut'>
);

export type FileChangeCallback = (event: FileChangeEvent) => Promise<void>;
export type ClipboardSelection = { type: 'copy' | 'cut'; name: string; kind: Dirent['type'] };

interface FileContextMenuProps {
  /**
   * Callback invoked when a file is changed.
   */
  onFileChange?: FileChangeCallback;

  /**
   * Callback to expand the node if collapsed.
   */
  onExpandNode?: () => void;

  /**
   * Callback invoked when clipboard actions are selected (copy, cut).
   */
  onClipboard: (value?: ClipboardSelection) => void;

  /**
   * Current item on the clipboard.
   */
  clipboardSelection: ClipboardSelection | undefined;

  /**
   * Location of the interaction.
   */
  node?: Node;

  /**
   * Whether the node is selected.
   */
  nodeIsSelected?: boolean;

  /**
   * For folders whether or not the folder is collapsed.
   */
  nodeIsCollapsed?: boolean;

  /**
   * Whether to render new files/directories before or after the trigger element.
   *
   * @default 'before'
   */
  position?: 'before' | 'after';

  /**
   * Props for trigger wrapper.
   */
  triggerProps?: ComponentProps<'div'>;
}

export const FileContextMenu = memo<PropsWithChildren<FileContextMenuProps>>(
  ({
    node,
    nodeIsSelected,
    nodeIsCollapsed,
    onFileChange,
    onExpandNode,
    onClipboard,
    clipboardSelection,
    position = 'before',
    triggerProps,
    children,
  }) => {
    const currentView = useStore(workbenchStore.currentView);
    const showChat = useStore(chatStore.showChat);
    const [state, setState] = useState<'idle' | 'add_file' | 'add_folder' | 'rename'>('idle');
    const inputRef = useRef<HTMLInputElement>(null);

    if (!onFileChange) {
      return children;
    }

    const copyPath = () => navigator.clipboard.writeText(node?.fullPath ?? WORK_DIR);

    const copyRelativePath = () => {
      return navigator.clipboard.writeText(node ? nodePath.relative(WORK_DIR, node.fullPath) : '');
    };

    const renameFileOrFolder = () => setState('rename');
    const newFile = () => (setState('add_file'), onExpandNode?.());
    const newFolder = () => (setState('add_folder'), onExpandNode?.());
    const cut = (value: Node) => onClipboard?.({ type: 'cut', name: value.fullPath, kind: value.kind });
    const copy = (value: Node) => onClipboard?.({ type: 'copy', name: value.fullPath, kind: value.kind });

    const changeFile: FileChangeCallback = async (event) => {
      await onFileChange(event).catch(onError);

      setState('idle');
    };

    const deleteFileOrFolder = () => {
      if (node) {
        changeFile({
          method: 'remove',
          path: node.fullPath,
          type: node.kind,
        });
      }
    };

    const paste = async () => {
      if (!clipboardSelection || !onClipboard) {
        return;
      }

      const fullPath = node?.fullPath ?? WORK_DIR;
      const parentDir = node?.kind === 'file' ? fullPath.slice(0, fullPath.lastIndexOf('/')) : fullPath;
      const filename = clipboardSelection.name.slice(1 + clipboardSelection.name.lastIndexOf('/'));

      await onFileChange({
        method: clipboardSelection.type,
        type: clipboardSelection.kind,
        oldPath: clipboardSelection.name,
        newPath: `${parentDir}/${filename}`,
      }).catch(onError);

      onClipboard(undefined);
      onExpandNode?.();
    };

    const resetOrAcceptChanges = (event: React.FocusEvent<HTMLInputElement, Element>) => {
      const value = event.currentTarget.value;

      if (value.trim() === '') {
        setState('idle');

        return;
      }

      fileNameEnd(value);
    };

    const fileNameEnd = (newName: string) => {
      if (state === 'idle') {
        return;
      }

      setState('idle');

      if (!newName) {
        toast.error('File name cannot be empty');
        return;
      }

      if (state === 'rename') {
        // do nothing if the name has not changed or if the component is used incorrectly
        if (!node || newName === node.name) {
          return;
        }

        const parentDir = node.fullPath.slice(0, node.fullPath.lastIndexOf('/'));

        changeFile({
          oldPath: node!.fullPath,
          newPath: `${parentDir}/${newName}`,
          type: node!.kind,
          method: 'rename',
        });

        return;
      }

      const parentDir =
        node?.kind === 'file'
          ? node.fullPath.slice(0, node.fullPath.lastIndexOf('/'))
          : node?.kind === 'folder'
            ? node.fullPath
            : WORK_DIR;

      changeFile({
        path: `${parentDir}/${newName}`,
        type: state === 'add_file' ? 'file' : 'folder',
        method: 'add',
      });
    };

    const fileNameKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        fileNameEnd(event.currentTarget.value);
      }

      if (event.key === 'Escape') {
        event.stopPropagation();
        setState('idle');
      }
    };

    const closeAutoFocus = (event: Event) => {
      if (state !== 'idle' && inputRef.current) {
        event.preventDefault();
        inputRef.current.focus();
      }
    };

    const element = (
      <Trigger asChild>
        <div {...triggerProps}>{children}</div>
      </Trigger>
    );

    const isVisible = currentView === 'code' || !showChat;
    const hasNode = !!node;

    let inputIcon;

    if (state === 'add_file' || (state === 'rename' && node?.kind === 'file')) {
      inputIcon = 'i-ph:file-duotone';
    } else if (state === 'add_folder' || (state === 'rename' && nodeIsCollapsed)) {
      inputIcon = 'i-ph:caret-right';
    } else {
      inputIcon = 'i-ph:caret-down';
    }

    let inputDepth = node?.depth ?? 0;

    // if we add a file or folder to a folder we increment the depth by one
    if (node && node.kind === 'folder' && state !== 'rename') {
      inputDepth += 1;
    }

    return (
      <Root>
        {position === 'before' && state !== 'rename' && element}

        {state !== 'idle' && (
          <div
            className={classNames(`group flex items-center gap-1.5 border-2 border-solid border-transparent`, {
              'bg-transparent text-bolt-elements-item-contentDefault hover:text-bolt-elements-item-contentActive hover:bg-bolt-elements-item-backgroundActive':
                !nodeIsSelected || state !== 'rename',
              'bg-bolt-elements-item-backgroundAccent text-bolt-elements-item-contentAccent':
                nodeIsSelected && state === 'rename',
            })}
            style={getDepthStyle(inputDepth)}
          >
            <div className={`scale-98 shrink-0 ${inputIcon}`} />
            <input
              ref={inputRef}
              defaultValue={state === 'rename' ? node?.name : undefined}
              spellCheck="false"
              autoComplete="off"
              data-1p-ignore
              type="text"
              onBlur={resetOrAcceptChanges}
              onKeyUp={fileNameKeyPress}
              className={classNames('text-bolt-elements-text-primary border-none w-full py-0.5 outline outline-1', {
                'outline-bolt-elements-item-outlineActive group-hover:bg-transparent bg-bolt-elements-item-backgroundActive':
                  !nodeIsSelected || state !== 'rename',
                'outline-bolt-elements-item-contentAccent bg-transparent': nodeIsSelected && state === 'rename',
              })}
            />
          </div>
        )}

        {position === 'after' && state !== 'rename' && element}

        <Portal>
          <Content
            onCloseAutoFocus={closeAutoFocus}
            className={classNames(
              'border border-bolt-elements-borderColor rounded-md',
              'z-context-menu bg-bolt-elements-background-depth-1 dark:bg-bolt-elements-background-depth-2 w-72',
              'data-[state=open]:animate-in animate-duration-100',
              'data-[state=open]:fade-in-0',
              'data-[state=open]:zoom-in-98',
              {
                'w-56': hasNode,
                'w-48': !node,
                hidden: !isVisible,
              },
            )}
          >
            <ActionSection>
              <MenuItem icon={hasNode} onClick={newFile}>
                New file...
              </MenuItem>
              <MenuItem icon={hasNode} onClick={newFolder}>
                New folder...
              </MenuItem>
            </ActionSection>

            {node && (
              <ActionSection>
                <MenuItemTarget node={node} />
                <MenuItemLock node={node} />
              </ActionSection>
            )}

            {(node || clipboardSelection) && (
              <ActionSection>
                {node && (
                  <MenuItem icon={hasNode} onClick={() => cut(node)}>
                    Cut
                  </MenuItem>
                )}
                {node && (
                  <MenuItem icon={hasNode} onClick={() => copy(node)}>
                    Copy
                  </MenuItem>
                )}
                {clipboardSelection && (
                  <MenuItem icon={hasNode} onClick={paste}>
                    Paste
                  </MenuItem>
                )}
              </ActionSection>
            )}

            <ActionSection last={!node}>
              <MenuItem icon={hasNode} onClick={copyPath}>
                Copy path
              </MenuItem>
              <MenuItem icon={hasNode} onClick={copyRelativePath}>
                Copy relative path
              </MenuItem>
            </ActionSection>

            {node && (
              <ActionSection last>
                <MenuItem icon={hasNode} onClick={renameFileOrFolder}>
                  Rename...
                </MenuItem>
                <MenuItem icon={hasNode} onClick={deleteFileOrFolder}>
                  Delete
                </MenuItem>
              </ActionSection>
            )}
          </Content>
        </Portal>
      </Root>
    );
  },
);

function onError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);

  if (error instanceof FilesystemError) {
    toast.error(message);
  } else {
    logger.error(message);

    toast.error('Unknown error');
  }
}

interface ActionSectionProps {
  last?: boolean;
}

function ActionSection({ children, last }: PropsWithChildren<ActionSectionProps>) {
  return (
    <Group className={classNames('p-1', { 'border-b-px border-solid border-bolt-elements-borderColor': !last })}>
      {children}
    </Group>
  );
}

interface MenuItemProps {
  icon?: boolean | string;
}

function MenuItem({ icon, children, ...props }: MenuItemProps & ComponentProps<typeof Item>) {
  return (
    <Item
      {...props}
      className="flex items-center gap-2 px-2 py-1.5 outline-0 text-sm text-bolt-elements-textPrimary cursor-pointer ws-nowrap text-bolt-elements-item-contentDefault hover:text-bolt-elements-item-contentActive hover:bg-bolt-elements-item-backgroundActive rounded-md"
    >
      {icon && <span className={`size-4 ${icon || ''} shrink-0`}></span>}
      <span>{children}</span>
    </Item>
  );
}

interface MenuItemTargetOrLockProps {
  node: Node;
}

const MenuItemTarget = memo<MenuItemTargetOrLockProps>(({ node }) => {
  const files = useStore(workbenchStore.files);
  const targetedFiles = useStore(workbenchStore.targetedFiles);

  const filesUnderNode = getAllFilesUnderNode(files, node);

  const areAllTargeted = filesUnderNode.length > 0 && filesUnderNode.every((filePath) => filePath in targetedFiles);

  const toggleTargetedState = () => {
    const newTargetState = node.kind === 'folder' ? !areAllTargeted : !node.dirent.isTargeted;

    workbenchStore.setFileOrFolderTarget(node.fullPath, newTargetState);
  };

  let menuText;

  if (node.kind === 'folder') {
    menuText = areAllTargeted ? 'Untarget all' : 'Target all';
  } else if (node.dirent.isTargeted) {
    menuText = 'Untarget file';
  } else {
    menuText = 'Target file';
  }

  return (
    <MenuItem onClick={toggleTargetedState} icon="i-ph:crosshair">
      {menuText}
    </MenuItem>
  );
});

const MenuItemLock = memo<MenuItemTargetOrLockProps>(({ node }) => {
  const files = useStore(workbenchStore.files);
  const lockedFiles = useStore(workbenchStore.lockedFiles);

  const filesUnderNode = getAllFilesUnderNode(files, node);

  const areAllLocked = filesUnderNode.length > 0 && filesUnderNode.every((filePath) => filePath in lockedFiles);

  const toggleLockState = () => {
    const newLockState = node.kind === 'folder' ? !areAllLocked : !node.dirent.isLocked;

    workbenchStore.setFileOrFolderLocked(node.fullPath, newLockState);
  };

  let menuText;

  if (node.kind === 'folder') {
    menuText = areAllLocked ? 'Unlock all' : 'Lock all';
  } else if (node.dirent.isLocked) {
    menuText = 'Unlock file';
  } else {
    menuText = 'Lock file';
  }

  return (
    <MenuItem onClick={toggleLockState} icon="i-ph:lock-simple-open">
      {menuText}
    </MenuItem>
  );
});

function getAllFilesUnderNode(files: FileMap, node: Node) {
  if (node.kind === 'file') {
    return [];
  }

  return Object.keys(files).filter((filePath) => {
    // ignore deleted files, all folders and files not under node.fullPath
    return files[filePath] && files[filePath].type === 'file' && filePath.startsWith(`${node.fullPath}/`);
  });
}

export function getDepthStyle(depth: number) {
  return { paddingLeft: `${6 + depth * 8}px` };
}
